import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Checkbox,
  Grid,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useCreateIframeMutation
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function BasicModal({ FetchItems, FetchStyles }) {
  const formRef = React.useRef();

  const [createIframe, resultStats] = useCreateIframeMutation();

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    styles: Yup.array().min(1, "Please select at least one style"),
    amazonFields: Yup.object().shape({
      amazonUrl: Yup.mixed().required("Amazon url is required"),
      amazonIframe: Yup.mixed().required("Amazon iframe is required"),
    }),
    items: Yup.array().min(1, "Please select at least one item"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Iframe has been created successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>
        Add Iframe
      </Button>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            initialValues={{
              styles: [],
              items: [],
              amazonFields: {
                amazonUrl: "",
                amazonIframe: "",
                itemDimensions: {
                  length: "",
                  width: "",
                  height: "",
                },
                price: "",
              },
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);

              const { styles, items, amazonFields } = values;
              let data = {
                styles,
                items,
                amazonFields,
              };

              createIframe(data);
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setValues,
            }) => (
              <Form noValidate autoComplete="off">
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  npmsvariants="h6"
                  mb={2}
                >
                  Create a Iframe
                </Typography>

                {FetchStyles && (
                  <Stack mt={2}>
                    <Autocomplete
                      onChange={(_, value) =>
                        setValues({ ...values, styles: value })
                      }
                      required
                      name="styles"
                      multiple
                      id="checkboxes-tags-demo"
                      options={FetchStyles}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option._id}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="styles"
                          label="Styles"
                          placeholder="Styles"
                          onBlur={handleBlur}
                          helperText={
                            getIn(touched, "styles") && getIn(errors, "styles")
                              ? getIn(errors, "styles")
                              : ""
                          }
                          error={Boolean(
                            getIn(touched, "styles") && getIn(errors, "styles")
                          )}
                        />
                      )}
                    />
                  </Stack>
                )}

                {FetchItems?.items && (
                  <Stack mt={2}>
                    <Autocomplete
                      onChange={(_, value) =>
                        setValues({ ...values, items: value })
                      }
                      required
                      name="items"
                      multiple
                      id="checkboxes-tags-demo"
                      options={FetchItems?.items}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option._id}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="items"
                          label="Items"
                          placeholder="Items"
                          onBlur={handleBlur}
                          helperText={
                            getIn(touched, "items") && getIn(errors, "items")
                              ? getIn(errors, "items")
                              : ""
                          }
                          error={Boolean(
                            getIn(touched, "items") && getIn(errors, "items")
                          )}
                        />
                      )}
                    />
                  </Stack>
                )}

                <Stack mt={2}>
                  <TextField
                    variant="outlined"
                    label="Amazon Url"
                    name={"amazonFields.amazonUrl"}
                    value={values?.amazonFields.amazonUrl}
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      getIn(touched, "amazonFields.amazonUrl") &&
                      getIn(errors, "amazonFields.amazonUrl")
                        ? getIn(errors, "amazonFields.amazonUrl")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "amazonFields.amazonUrl") &&
                        getIn(errors, "amazonFields.amazonUrl")
                    )}
                  />
                </Stack>

                <Stack mt={2}>
                  <TextField
                    variant="outlined"
                    multiline
                    rows={7}
                    label="Amazon Iframe"
                    name={"amazonFields.amazonIframe"}
                    value={values.amazonFields.amazonIframe}
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      getIn(touched, "amazonFields.amazonIframe") &&
                      getIn(errors, "amazonFields.amazonIframe")
                        ? getIn(errors, "amazonFields.amazonIframe")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "amazonFields.amazonIframe") &&
                        getIn(errors, "amazonFields.amazonIframe")
                    )}
                  />
                </Stack>

                <Grid container mt={2} spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Length"
                      name={"amazonFields.itemDimensions.length"}
                      value={values?.amazonFields.itemDimensions.length}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        getIn(touched, "amazonFields.itemDimensions.length") &&
                        getIn(errors, "amazonFields.itemDimensions.length")
                          ? getIn(errors, "amazonFields.itemDimensions.length")
                          : ""
                      }
                      error={Boolean(
                        getIn(touched, "amazonFields.itemDimensions.length") &&
                          getIn(errors, "amazonFields.itemDimensions.length")
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Width"
                      name={"amazonFields.itemDimensions.width"}
                      value={values?.amazonFields.itemDimensions.width}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        getIn(touched, "amazonFields.itemDimensions.width") &&
                        getIn(errors, "amazonFields.itemDimensions.width")
                          ? getIn(errors, "amazonFields.itemDimensions.width")
                          : ""
                      }
                      error={Boolean(
                        getIn(touched, "amazonFields.itemDimensions.width") &&
                          getIn(errors, "amazonFields.itemDimensions.width")
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Height"
                      name={"amazonFields.itemDimensions.height"}
                      value={values?.amazonFields.itemDimensions.height}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        getIn(touched, "amazonFields.itemDimensions.height") &&
                        getIn(errors, "amazonFields.itemDimensions.height")
                          ? getIn(errors, "amazonFields.itemDimensions.height")
                          : ""
                      }
                      error={Boolean(
                        getIn(touched, "amazonFields.itemDimensions.height") &&
                          getIn(errors, "amazonFields.itemDimensions.height")
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Price"
                      name={"amazonFields.price"}
                      value={values?.amazonFields.price}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        getIn(touched, "amazonFields.price") &&
                        getIn(errors, "amazonFields.price")
                          ? getIn(errors, "amazonFields.price")
                          : ""
                      }
                      error={Boolean(
                        getIn(touched, "amazonFields.price") &&
                          getIn(errors, "amazonFields.price")
                      )}
                    />
                  </Grid>
                </Grid>

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                  >
                    Add Iframe
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
